import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { claimAccess } from "@/lib/api";
import { getDeviceId } from "@/lib/device";

export default function Success() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("form"); // form | loading | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const [state, setState] = useState(null);

  const handleClaim = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const s = await claimAccess(email.trim(), getDeviceId());
      setState(s);
      setStatus("ok");
    } catch (err) {
      const msg = err?.response?.data?.detail ||
        "Não foi possível liberar o acesso. Tente novamente em instantes.";
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  const until = state?.access_expires_at
    ? new Date(state.access_expires_at).toLocaleDateString("pt-BR")
    : null;

  return (
    <main className="min-h-screen px-4 pb-20 pt-28 sm:px-6">
      <div className="mx-auto max-w-md text-center">
        <div className="glass rounded-3xl p-8">
          {status === "ok" ? (
            <div className="flex flex-col items-center gap-4" data-testid="success-activated">
              <CheckCircle2 className="h-14 w-14 text-emerald-400" />
              <h1 className="font-display text-2xl font-extrabold text-white">Acesso liberado!</h1>
              <p className="text-sm text-slate-300">
                Seu acesso completo ao Pronto.AI está ativo por 1 ano
                {until ? `, até ${until}` : ""}. Agora você pode fazer novas perguntas sem limite.
              </p>
              <Link to="/ferramenta" className="w-full no-underline">
                <Button
                  data-testid="success-go-tool"
                  className="mt-2 h-auto w-full rounded-full bg-emerald-500 py-4 text-base font-bold text-slate-950 hover:bg-emerald-400"
                >
                  Começar a usar <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleClaim} className="flex flex-col gap-4 text-left">
              <div className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500/20 to-cyan-400/20 text-violet-300">
                  <Mail className="h-7 w-7" />
                </div>
                <h1 className="mt-4 font-display text-2xl font-extrabold text-white">
                  Liberar meu acesso
                </h1>
                <p className="mt-2 text-sm text-slate-300">
                  Digite o <strong className="text-white">mesmo e-mail</strong> que você usou na
                  compra. Assim que o pagamento for confirmado, seu acesso de 1 ano é liberado.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="claim-email" className="text-sm font-medium text-slate-200">
                  E-mail da compra
                </Label>
                <Input
                  id="claim-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  data-testid="claim-email-input"
                  className="rounded-xl border-white/10 bg-slate-950/50 text-slate-100 placeholder:text-slate-500 focus-visible:ring-violet-500"
                />
              </div>

              {status === "error" && (
                <p data-testid="claim-error" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
                  {errorMsg}
                </p>
              )}

              <Button
                type="submit"
                disabled={status === "loading"}
                data-testid="claim-submit-button"
                className="h-auto w-full rounded-full bg-emerald-500 py-4 text-base font-bold text-slate-950 hover:bg-emerald-400 disabled:opacity-70"
              >
                {status === "loading" ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verificando…</>
                ) : (
                  "LIBERAR MEU ACESSO"
                )}
              </Button>

              <p className="text-center text-xs text-slate-500">
                O acesso é liberado somente após a confirmação real do pagamento pela plataforma de
                checkout. Se acabou de comprar, pode levar alguns minutos.
              </p>
              <Link to="/ferramenta" className="text-center text-sm font-medium text-violet-300 hover:text-violet-200 no-underline">
                Voltar à ferramenta
              </Link>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
